import React, { useEffect, useRef, useState } from 'react';
import api from '../api/api';

const localInput = date => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export default function CrmReminder({ message, onClose, onSaved, snooze = false }) {
  const dialog = useRef(null);
  const [dueAt, setDueAt] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [scheduled, setScheduled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const endpoint = `/crm-inbox/${message.id}/reminder`;
  useEffect(() => {
    const element = dialog.current;
    element.showModal();
    let current = true;
    api.get(endpoint).then(({ data }) => {
      if (current) { setScheduled(data.dueAt); setDueAt(data.dueAt ? localInput(data.dueAt) : ''); }
    }).catch(err => { if (current) setError(err.response?.data?.error || 'Unable to load reminder.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; element.close(); };
  }, [endpoint]);
  const save = async (event, cancel = false) => {
    event.preventDefault();
    if (saving) return;
    const date = new Date(dueAt);
    if (!cancel && (!dueAt || !Number.isFinite(date.getTime()) || date <= new Date())) {
      setError('Choose a future date and time.'); return;
    }
    setSaving(true); setError('');
    try {
      if (cancel) await api.delete(endpoint);
      else if (snooze) await api.put(`/crm-inbox/${message.id}/snooze`, { dueAt: date.toISOString(), version: message.version, reminderGeneration: message.reminderGeneration || 0 });
      else await api.put(endpoint, { dueAt: date.toISOString() });
      onSaved?.(cancel ? null : date.toISOString());
      window.dispatchEvent(new Event('crm-inbox-changed'));
      window.dispatchEvent(new Event('crm-reminder-changed'));
      onClose();
    } catch (err) { setError(err.response?.data?.error || 'Unable to save reminder.'); }
    finally { setSaving(false); }
  };
  return <dialog ref={dialog} className="crm-history-modal crm-reminder-modal" aria-labelledby="crm-reminder-title" onCancel={event => { if (saving) event.preventDefault(); else onClose(); }}>
    <header><div><h3 id="crm-reminder-title">{snooze ? 'Snooze message' : 'Schedule reminder'}</h3><p>{snooze ? 'Hide this message from your inbox until the selected time. It will return as New.' : 'This message will return to your inbox as New at the scheduled time.'}</p></div><button type="button" className="crm-history-close" aria-label="Close reminder" disabled={saving} onClick={onClose}>×</button></header>
    <form className="crm-reminder-form" onSubmit={save}>
      <p className="crm-body">{message.body}</p>
      {loading ? <p>Loading reminder...</p> : <>
        {scheduled && <p>Current reminder: {new Date(scheduled).toLocaleString()}</p>}
        <label htmlFor="crm-reminder-date">Reminder date and time</label>
        {snooze && <div className="crm-actions">{[['1 hour', 1], ['Tomorrow', 24], ['Next week', 168]].map(([label, hours]) => <button key={label} type="button" className="crm-snooze-preset" aria-pressed={selectedPreset === hours} disabled={saving} onClick={() => { const date = new Date(); if (hours === 1) date.setHours(date.getHours() + 1); else date.setDate(date.getDate() + hours / 24); setDueAt(localInput(date)); setSelectedPreset(hours); }}>{label}</button>)}</div>}
        <input id="crm-reminder-date" type="datetime-local" value={dueAt} min={localInput(Date.now() + 60000)} onChange={event => { setDueAt(event.target.value); setSelectedPreset(null); }} disabled={saving} required />
        <small>Time zone: {Intl.DateTimeFormat().resolvedOptions().timeZone}. Only your inbox is affected.</small>
        <div className="crm-actions">{scheduled && !snooze && <button type="button" disabled={saving} onClick={event => save(event, true)}>Cancel reminder</button>}<button type="submit" disabled={saving || !dueAt}>{saving ? 'Saving...' : snooze ? 'Snooze' : 'Save reminder'}</button></div>
      </>}
      {error && <p className="crm-error" role="alert">{error}</p>}
    </form>
  </dialog>;
}
